import {
	dedupeAppIds,
	getOutboundLinkAppId,
	type BylineAppId,
} from '../utils/bylineAppIdentity.ts';
import type { BylineSmartLinksQuery$data } from '../queries/__generated__/BylineSmartLinksQuery.graphql.ts';

export type OutboundBylineSsrStatus = 'present' | 'empty' | 'unavailable';

// How many of the page's links the outbound sample reads. The byline's placeholder counts distinct
// apps, so one page of links is needed to see past the first few; this also matches the page size
// the inbound sample uses. CCINT-9543
export const OUTBOUND_BYLINE_SAMPLE_LIMIT = 50;

// `linkCount` is the number of sampled Smart Links (at most `OUTBOUND_BYLINE_SAMPLE_LIMIT`), which
// `shouldPaintBylineSsrPlaceholder` adds to the inbound and related samples to decide whether the
// page reaches the byline's row minimum (CCINT-9359). `appIds` are the distinct apps those links
// identified, in page order, which `getBylinePlaceholderIconCount` unions across the sources to
// size the placeholder (CCINT-9543).
export type OutboundBylineSsrState = Readonly<
	| {
			contentId: string;
			status: 'present';
			linkCount: number;
			appIds: readonly BylineAppId[];
	  }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export function createOutboundBylineSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: 'present',
	linkCount: number,
	appIds?: readonly BylineAppId[],
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: OutboundBylineSsrStatus,
	linkCount?: number,
	appIds: readonly BylineAppId[] = [],
): OutboundBylineSsrState {
	if (status === 'present') {
		return Object.freeze({
			contentId,
			status,
			linkCount: linkCount ?? 0,
			appIds: Object.freeze([...appIds]),
		});
	}
	return Object.freeze({ contentId, status });
}

export const normalizeOutboundBylineSsrState = (
	contentId: string,
	data: BylineSmartLinksQuery$data | null | undefined,
): OutboundBylineSsrState => {
	const nodes = data?.contentSmartLinks?.nodes;
	if (nodes === null || nodes === undefined) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	// Malformed data does not establish a reliable connection signal.
	const hasMalformedNode = nodes.some(
		(node) => typeof node?.url !== 'string' || node.url.length === 0,
	);
	if (hasMalformedNode) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	if (nodes.length === 0) {
		return createOutboundBylineSsrState(contentId, 'empty');
	}

	const appIds = dedupeAppIds(
		nodes.map((node) =>
			node ? getOutboundLinkAppId({ url: node.url, product: node.product }) : null,
		),
	);
	return createOutboundBylineSsrState(contentId, 'present', nodes.length, appIds);
};
